/* eslint-disable @typescript-eslint/naming-convention */
import {Injectable} from '@angular/core';
import {CommandMap, CommandsDescriptor} from '@app/models/commands/commands-descriptor';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {ClassroomMessage, MessageRecord, MsgRecordType, RecordStack} from '@app/services/data.service';
import {Store} from '@ngxs/store';
import {ExecBuffer} from '@app/models/exec-buffer';
import {GeoGebraService} from '@app/modules/whiteboard/services/geo-gebra.service';

export enum WhiteboardEvents {
  DRAW_MOUSE_DOWN = 'DRAW_MOUSE_DOWN',
  DRAW_MOUSE_MOVE = 'DRAW_MOUSE_MOVE',
  DRAW_MOUSE_UP = 'DRAW_MOUSE_UP',
  PERFECT_MODE_ON = 'PERFECT_MODE_ON',
  PERFECT_MODE_OFF = 'PERFECT_MODE_OFF',
  STOP_CLICK_DRAWING = 'STOP_CLICK_DRAWING',
  ELEMENTS_CREATED = 'ELEMENTS_CREATED',

  POST_PROBLEM = 'POST_PROBLEM',

  ELEMENTS_FOCUSED = 'ELEMENTS_FOCUSED',
  DRAG_MOUSE_DOWN = 'DRAG_MOUSE_DOWN',
  DRAG_MOUSE_MOVE = 'DRAG_MOUSE_MOVE',
  DRAG_MOUSE_UP = 'DRAG_MOUSE_UP',
  ELEMENTS_DRAGGED = 'ELEMENTS_DRAGGED',

  NODE_EDIT_MOUSE_DOWN = 'NODE_EDIT_MOUSE_DOWN',
  NODE_EDIT_MOUSE_MOVE = 'NODE_EDIT_MOUSE_MOVE',
  NODE_EDIT_MOUSE_UP = 'NODE_EDIT_MOUSE_UP',
  NODE_EDITED = 'NODE_EDITED',

  TABLE_EDIT_MOUSE_DOWN = 'TABLE_EDIT_MOUSE_DOWN',
  TABLE_EDIT_MOUSE_MOVE = 'TABLE_EDIT_MOUSE_MOVE',
  TABLE_EDIT_MOUSE_UP = 'TABLE_EDIT_MOUSE_UP',
  TABLE_EDITED = 'TABLE_EDITED',
  TABLE_RECREATED = 'TABLE_RECREATED',
  SET_TABLE = 'SET_TABLE',
  ADD_TABLE_ROW = 'ADD_TABLE_ROW',
  ADD_TABLE_COL = 'ADD_TABLE_COL',
  REMOVE_TABLE_ROW = 'REMOVE_TABLE_ROW',
  REMOVE_TABLE_COL = 'REMOVE_TABLE_COL',

  RESIZE_MOUSE_DOWN = 'RESIZE_MOUSE_DOWN',
  RESIZE_MOUSE_MOVE = 'RESIZE_MOUSE_MOVE',
  RESIZE_MOUSE_UP = 'RESIZE_MOUSE_UP',
  ELEMENTS_RESIZED = 'ELEMENTS_RESIZED',

  REF_POINT_CHANGED = 'REF_POINT_CHANGED',
  RECENTER_REF_POINT = 'RECENTER_REF_POINT',

  ROTATE_MOUSE_DOWN = 'ROTATE_MOUSE_DOWN',
  ROTATE_MOUSE_MOVE = 'ROTATE_MOUSE_MOVE',
  ROTATE_MOUSE_UP = 'ROTATE_MOUSE_UP',
  ELEMENTS_ROTATED = 'ELEMENTS_ROTATED',

  HIGHLIGHT_MOUSE_DOWN = 'HIGHLIGHT_MOUSE_DOWN',
  HIGHLIGHT_MOUSE_MOVE = 'HIGHLIGHT_MOUSE_MOVE',
  HIGHLIGHT_MOUSE_UP = 'HIGHLIGHT_MOUSE_UP',
  HIGHLIGHTED = 'HIGHLIGHTED',

  POINTER_TOOL = 'POINTER_TOOL',
  POINTER_TOOL_OFF = 'POINTER_TOOL_OFF',
  POINTER_MOUSE_MOVE = 'POINTER_MOUSE_MOVE',

  STYLE_CHANGE = 'STYLE_CHANGE',
  TEXT_TYPING = 'TEXT_TYPING',
  ASSET_EDITING = 'ASSET_EDITING',
  NUMBER_LINE_EDITED = 'NUMBER_LINE_EDITED',
  COORDINATE_PLANE_EDITED = 'COORDINATE_PLANE_EDITED',
  ELEMENTS_DELETED = 'ELEMENTS_DELETED',
  TO_TOP = 'TO_TOP',
  TO_BOTTOM = 'TO_BOTTOM',

  CHANGE_PROBLEM_RESIZE_MODE = 'CHANGE_PROBLEM_RESIZE_MODE',

  GROUP = 'GROUP',
  UNGROUP = 'UNGROUP',

  LOCAL_COPY = 'LOCAL_COPY',

  RESET = 'RESET',

  UNDO = 'UNDO',
  REDO = 'REDO',

  REFRESH = 'REFRESH',

  PURGE = 'PURGE',


  /* GeoGebra events */
  POST_GGB_APPLET = 'POST_GGB_APPLET',
  EDIT_GGB_APPLET = 'EDIT_GGB_APPLET',
  GGB_ADD_EVENT = 'GGB_ADD_EVENT',
  GGB_RESIZE = 'GGB_RESIZE'
}

export enum WhiteboardStates {
  GRID = 'GRID',
  SNAP = 'SNAP',
  SNAP_SIDE = 'SNAP_SIDE',

  HEIGHT = 'HEIGHT',
  SCROLL = 'SCROLL',

  STREAMING = 'STREAMING',
  SMOOTH = 'SMOOTH',

  SHARE = 'SHARE',
  SHARE_WITH = 'SHARE_WITH',
  SHARED_USERS = 'SHARED_USERS',
  UNSHARE = 'UNSHARE',
  UNSHARE_WITH = 'UNSHARE_WITH',


  /* GeoGebra states */
}


@Injectable({
  providedIn: 'root'
})
export class WhiteboardCommands extends CommandsDescriptor<WhiteboardEvents, WhiteboardStates> {

  static category = 'WHITEBOARD';

  private geoGebraService: GeoGebraService;

  constructor(protected whiteboardService: WhiteboardService, store: Store) {
    const MAP: CommandMap<WhiteboardEvents, WhiteboardStates> = {

      EVENT: {
        DRAW_MOUSE_DOWN: (record: MessageRecord) => this.whiteboardService.onDrawMouseDown(record),
        DRAW_MOUSE_MOVE: (record: MessageRecord) => this.whiteboardService.onDrawMouseMove(record),
        DRAW_MOUSE_UP: (record: MessageRecord) => this.whiteboardService.onDrawMouseUp(record),
        PERFECT_MODE_ON: (record: MessageRecord) => this.whiteboardService.onPerfectModeOn(record),
        PERFECT_MODE_OFF: (record: MessageRecord) => this.whiteboardService.onPerfectModeOff(record),
        STOP_CLICK_DRAWING: (record: MessageRecord) => this.whiteboardService.onStopClickDrawing(record),
        ELEMENTS_CREATED: (record: MessageRecord) => this.whiteboardService.onElementsCreated(record),

        POST_PROBLEM: (record: MessageRecord) => this.whiteboardService.onPostProblem(record),

        ELEMENTS_FOCUSED: (record: MessageRecord) => this.whiteboardService.onElementsFocused(record),
        DRAG_MOUSE_DOWN: (record: MessageRecord) => this.whiteboardService.onDragMouseDown(record),
        DRAG_MOUSE_MOVE: (record: MessageRecord) => this.whiteboardService.onDragMouseMove(record),
        DRAG_MOUSE_UP: (record: MessageRecord) => this.whiteboardService.onDragMouseUp(record),
        ELEMENTS_DRAGGED: (record: MessageRecord) => this.whiteboardService.onElementsDragged(record),

        NODE_EDIT_MOUSE_DOWN: (record: MessageRecord) => this.whiteboardService.onNodeEditMouseDown(record),
        NODE_EDIT_MOUSE_MOVE: (record: MessageRecord) => this.whiteboardService.onNodeEditMouseMove(record),
        NODE_EDIT_MOUSE_UP: (record: MessageRecord) => this.whiteboardService.onNodeEditMouseUp(record),
        NODE_EDITED: (record: MessageRecord) => this.whiteboardService.onNodeEdited(record),

        TABLE_EDIT_MOUSE_DOWN: (record: MessageRecord) => this.whiteboardService.onTableEditMouseDown(record),
        TABLE_EDIT_MOUSE_MOVE: (record: MessageRecord) => this.whiteboardService.onTableEditMouseMove(record),
        TABLE_EDIT_MOUSE_UP: (record: MessageRecord) => this.whiteboardService.onTableEditMouseUp(record),
        TABLE_EDITED: (record: MessageRecord) => this.whiteboardService.onTableEdited(record),
        TABLE_RECREATED: (record: MessageRecord) => this.whiteboardService.onTableRecreated(record),
        SET_TABLE: (record: MessageRecord) => this.whiteboardService.onSetTable(record),
        ADD_TABLE_ROW: (record: MessageRecord) => this.whiteboardService.onAddTableRow(record),
        ADD_TABLE_COL: (record: MessageRecord) => this.whiteboardService.onAddTableCol(record),
        REMOVE_TABLE_ROW: (record: MessageRecord) => this.whiteboardService.onRemoveTableRow(record),
        REMOVE_TABLE_COL: (record: MessageRecord) => this.whiteboardService.onRemoveTableCol(record),

        RESIZE_MOUSE_DOWN :(record: MessageRecord) => this.whiteboardService.onResizeMouseDown(record),
        RESIZE_MOUSE_MOVE :(record: MessageRecord) => this.whiteboardService.onResizeMouseMove(record),
        RESIZE_MOUSE_UP :(record: MessageRecord) => this.whiteboardService.onResizeMouseUp(record),
        ELEMENTS_RESIZED :(record: MessageRecord) => this.whiteboardService.onElementsResized(record),

        REF_POINT_CHANGED: (record: MessageRecord) => this.whiteboardService.onRefPointChanged(record),
        RECENTER_REF_POINT: (record: MessageRecord) => this.whiteboardService.onRecenterRefPoint(record),

        ROTATE_MOUSE_DOWN: (record: MessageRecord) => this.whiteboardService.onRotateMouseDown(record),
        ROTATE_MOUSE_MOVE: (record: MessageRecord) => this.whiteboardService.onRotateMouseMove(record),
        ROTATE_MOUSE_UP: (record: MessageRecord) => this.whiteboardService.onRotateMouseUp(record),
        ELEMENTS_ROTATED: (record: MessageRecord) => this.whiteboardService.onElementsRotated(record),

        HIGHLIGHT_MOUSE_DOWN: (record: MessageRecord) => this.whiteboardService.onHighlightMouseDown(record),
        HIGHLIGHT_MOUSE_MOVE: (record: MessageRecord) => this.whiteboardService.onHighlightMouseMove(record),
        HIGHLIGHT_MOUSE_UP: (record: MessageRecord) => this.whiteboardService.onHighlightMouseUp(record),
        HIGHLIGHTED: (record: MessageRecord) => this.whiteboardService.onHighlighted(record),

        POINTER_TOOL: (record: MessageRecord) => this.whiteboardService.onPointerToolOn(record),
        POINTER_TOOL_OFF: (record: MessageRecord) => this.whiteboardService.onPointerToolOff(record),
        POINTER_MOUSE_MOVE: (record: MessageRecord) => this.whiteboardService.onPointerMouseMove(record),

        STYLE_CHANGE: (record: MessageRecord) => this.whiteboardService.onStyleChange(record),
        TEXT_TYPING: (record: MessageRecord) => this.whiteboardService.onTextTyping(record),
        ASSET_EDITING: (record: MessageRecord) => this.whiteboardService.onAssetEdit(record),
        NUMBER_LINE_EDITED: (record: MessageRecord) => this.whiteboardService.onNumberLineEdit(record),
        COORDINATE_PLANE_EDITED: (record: MessageRecord) => this.whiteboardService.onCoordinatePlaneEdit(record),
        ELEMENTS_DELETED: (record: MessageRecord) => this.whiteboardService.onElementsDeleted(record),
        TO_TOP: (record: MessageRecord) => this.whiteboardService.onToTop(record),
        TO_BOTTOM: (record: MessageRecord) => this.whiteboardService.onToBottom(record),

        CHANGE_PROBLEM_RESIZE_MODE: (record: MessageRecord) => this.whiteboardService.onProblemResizeMode(record),

        GROUP: (record: MessageRecord) => this.whiteboardService.onGroup(record),
        UNGROUP: (record: MessageRecord) => this.whiteboardService.onUngroup(record),

        LOCAL_COPY: () => {}, /* don't have a handler. LOCAL_COPY event sends only for logging */

        RESET: () => this.whiteboardService.onReset(),

        UNDO: () => this.whiteboardService.onUndo(),
        REDO: () => this.whiteboardService.onRedo(),

        REFRESH: (record: MessageRecord) => this.whiteboardService.onRefresh(record),
        PURGE: (record: MessageRecord) => this.whiteboardService.onPurge(record),

        POST_GGB_APPLET: (record: MessageRecord) => this.geoGebraService.onPostGGBApplet(record),
        EDIT_GGB_APPLET: (record: MessageRecord) => this.geoGebraService.onEditGGBApplet(record),
        GGB_ADD_EVENT: (record: MessageRecord) => this.geoGebraService.onGGBAddEvent(record),
        GGB_RESIZE: (record: MessageRecord) => this.geoGebraService.onGGBResize(record),
      },
      STATE: {
        GRID: (record: MessageRecord) => this.whiteboardService.onGrid(record),
        SNAP: (record: MessageRecord) => this.whiteboardService.onSnap(record),
        SNAP_SIDE: (record: MessageRecord) => this.whiteboardService.onSnapSideChange(record),

        HEIGHT: (record: MessageRecord) => this.whiteboardService.onHeight(record),
        SCROLL: (record: MessageRecord) => this.whiteboardService.onScroll(record),

        STREAMING: (record: MessageRecord) => this.whiteboardService.onStreaming(record),
        SMOOTH: (record: MessageRecord) => this.whiteboardService.onSmooth(record),

        SHARE: (record: MessageRecord) => this.whiteboardService.onShare(record),
        SHARE_WITH: (record: MessageRecord) => this.whiteboardService.onShareWith(record),
        SHARED_USERS: (record: MessageRecord) => this.whiteboardService.onShareWith(record),
        UNSHARE: () => this.whiteboardService.onUnshare(),
        UNSHARE_WITH: (record: MessageRecord) => this.whiteboardService.onUnshareWith(record),
      }
    };
    super(MAP, new ExecBuffer(true), store);
    this.whiteboardService.handleRecord = this.handleRecord.bind(this);
    this.whiteboardService.handleRecordStack = this.handleRecordStack.bind(this);
  }

  public override handleMessage(message: ClassroomMessage): void {
    if (message.record.type === MsgRecordType.EVENT && message.store) {
      this.whiteboardService.localStack.addEvent(message.record as MessageRecord);
    }

    super.handleMessage(message);
  }

  public override async handleRecordStack(recordStack: RecordStack): Promise<void> {
    let pointer = recordStack.events.length;

    await recordStack.states
      .sort((a, b) => {
        return a.timestamp - b.timestamp;   // sort by increasing
      }).reduce((previousPromise: Promise<any>, messageRecord: MessageRecord) =>
        previousPromise.then((): Promise<any> => {
          if (messageRecord.name === 'undoOffset') {
            pointer = parseInt(messageRecord.data);
            return Promise.resolve();
          } else if (messageRecord.name === 'numTeachersPointers') {
            this.whiteboardService.setStaffPointerCount(parseInt(messageRecord.data));
            return Promise.resolve();
          } else {
            return this.messageRecordToPromise(messageRecord);
          }
        }), Promise.resolve()
      );

    await recordStack.events
      .slice(0, pointer)
      .reduce((previousPromise: Promise<any>, messageRecord: MessageRecord) =>
          previousPromise.then((): Promise<any> =>
            this.messageRecordToPromise(messageRecord)
          ),
        Promise.resolve()
      );

    const timestamps: number[] = [...recordStack.events, ...recordStack.states]
      .map((record: MessageRecord) => record.timestamp);

    const maxTimestamp: number = Math.max(...timestamps);

    this.whiteboardService.updateLocalStack(recordStack.events, pointer);
    this.whiteboardService.clearEmptyTextBoxes();
    this.buffer.normalizeAndExecQueue(maxTimestamp);
  }

  public setGeoGebraService(geoGebraService: GeoGebraService): void {
    this.geoGebraService = geoGebraService;
  }
}
